import {firestore} from "./firebaseConfig.jsx";
import { uploadToCloudinary } from "./cloudinaryConfig.jsx";
import { validateImageFile } from "./imageUtils.jsx";

import { 
    addDoc, 
    collection, 
    onSnapshot, 
    doc, 
    updateDoc,
    query, 
    where, 
    setDoc, 
    deleteDoc,
    getDoc,
    getDocs
} from 'firebase/firestore';
import { getDatabase, ref, onValue } from "firebase/database";

let MenuCardRef = collection(firestore, "MenuCard");
let CarouselRef = collection(firestore, "Carousel");
let BasicInfoRef = collection(firestore, "BasicInfo");
let AdminCredentialsRef = collection(firestore, "AdminCredentials");

// Authentication functions
export const checkAdminCredentials = async (credentials) => {
    try {
        const adminQuery = query(
            AdminCredentialsRef,
            where("username", "==", credentials.username),
            where("password", "==", credentials.password)
        );
        
        const querySnapshot = await getDocs(adminQuery);
        
        if (!querySnapshot.empty) {
            // Credentials are valid
            return { success: true, user: querySnapshot.docs[0].data() };
        } else {
            // Credentials are invalid
            return { success: false, message: "Invalid credentials" };
        }
    } catch (error) {
        console.error("Error checking admin credentials: ", error);
        throw error;
    }
};



//user functions

export const getMenuInfo = (setAllMenuItems) => {
    try {
        let menuCardQuery = query(MenuCardRef);
        
        onSnapshot(menuCardQuery, (response) => {
            try {
                const items = response.docs.map((doc) => {
                    return { ...doc.data(), id: doc.id }
                });
                setAllMenuItems(items);
            } catch (error) {
                console.error('Error processing menu data:', error);
                setAllMenuItems([]);
            }
        }, (error) => {
            console.error('Error listening to menu data:', error);
            setAllMenuItems([]);
        });
    } catch (error) {
        console.error('Error setting up menu listener:', error);
        setAllMenuItems([]);
    }
}

export const getBasicInfo = (setBasicInfo) => {
    try {
        let basicInfoQuery = query(BasicInfoRef);
        
        onSnapshot(basicInfoQuery, (response) => {
            try {
                const basicInfoData = response.docs.map((doc) => {
                    return { ...doc.data(), id: doc.id }
                });
                // Since BasicInfo typically has only one document, we'll take the first one
                setBasicInfo(basicInfoData.length > 0 ? basicInfoData[0] : null);
            } catch (error) {
                console.error('Error processing basic info:', error);
                setBasicInfo(null);
            }
        }, (error) => {
            console.error('Error listening to basic info:', error);
            setBasicInfo(null);
        });
    } catch (error) {
        console.error('Error setting up basic info listener:', error);
        setBasicInfo(null);
    }
}

export const getCarouselSlides = (setCarouselSlides) => {
    try {
        let carouselQuery = query(CarouselRef);
        
        onSnapshot(carouselQuery, (response) => {
            try {
                const slides = response.docs.map((doc) => {
                    return { ...doc.data(), id: doc.id }
                });
                setCarouselSlides(slides);
            } catch (error) {
                console.error('Error processing carousel data:', error);
                setCarouselSlides([]);
            }
        }, (error) => {
            console.error('Error listening to carousel data:', error);
            setCarouselSlides([]);
        });
    } catch (error) {
        console.error('Error setting up carousel listener:', error);
        setCarouselSlides([]);
    }
}



//admin functions


// Add a new menu item to Firebase
export const addMenuItem = async (itemData) => {
    try {
        const hasWeightOptions = itemData.weightOptions && itemData.weightOptions.length > 0;
        
        const docRef = await addDoc(MenuCardRef, {
            name: itemData.name,
            category: itemData.category,
            dietaryPreference: itemData.dietaryPreference,
            hasWeightOptions: hasWeightOptions,
            weightOptions: hasWeightOptions ? itemData.weightOptions : [],
            singlePrice: hasWeightOptions ? '' : (itemData.singlePrice || ''),
            imageUrl: itemData.imageUrl,
            imageUrls: itemData.imageUrls || (itemData.imageUrl ? [itemData.imageUrl] : []),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        
        
        return { ...itemData, id: docRef.id };
    } catch (error) {
        console.error("Error adding menu item: ", error);
        throw error;
    }
};

// Update an existing menu item in Firebase
export const updateMenuItem = async (itemId, itemData) => {
    try {
        const hasWeightOptions = itemData.weightOptions && itemData.weightOptions.length > 0;
        
        const itemRef = doc(firestore, "MenuCard", itemId);
        await updateDoc(itemRef, {
            name: itemData.name,
            category: itemData.category,
            dietaryPreference: itemData.dietaryPreference,
            hasWeightOptions: hasWeightOptions,
            weightOptions: hasWeightOptions ? itemData.weightOptions : [],
            singlePrice: hasWeightOptions ? '' : (itemData.singlePrice || ''),
            imageUrl: itemData.imageUrl,
            imageUrls: itemData.imageUrls || (itemData.imageUrl ? [itemData.imageUrl] : []),
            updatedAt: new Date().toISOString()
        });
        
        return { ...itemData, id: itemId };
    } catch (error) {
        console.error("Error updating menu item: ", error);
        throw error;
    }
};

// Delete a menu item from Firebase
export const deleteMenuItem = async (itemId) => {
    try {
        const itemRef = doc(firestore, "MenuCard", itemId);
        await deleteDoc(itemRef);
        
        //console.log("Menu item deleted with ID: ", itemId);
        return true;
    } catch (error) {
        console.error("Error deleting menu item: ", error);
        throw error;
    }
};

// Get a single menu item by ID
export const getMenuItemById = async (itemId) => {
    try {
        const itemRef = doc(firestore, "MenuCard", itemId);
        const itemDoc = await getDoc(itemRef);
        
        if (itemDoc.exists()) {
            return { ...itemDoc.data(), id: itemDoc.id };
        } else {
            console.log("No such menu item!");
            return null;
        }
    } catch (error) {
        console.error("Error getting menu item: ", error);
        throw error;
    }
};

// Upload image to Cloudinary using direct upload
export const uploadImageToCloudinary = async (file) => {
    try {
        // Validate file using utility function
        const validationErrors = validateImageFile(file);
        if (validationErrors.length > 0) {
            throw new Error(validationErrors.join(', '));
        }
        
        //console.log('Starting Cloudinary upload for file:', file.name);
        
        const secureUrl = await uploadToCloudinary(file);
        
        // console.log('Image uploaded successfully to Cloudinary');
        
        
        return secureUrl;
    } catch (error) {
        console.error("Error uploading image to Cloudinary: ", error);
        throw error;
    }
};

// Upload carousel image to Cloudinary
export const uploadCarouselImageToCloudinary = async (file) => {
    try {
        // Validate file using utility function
        const validationErrors = validateImageFile(file);
        if (validationErrors.length > 0) {
            throw new Error(validationErrors.join(', '));
        }
        
        
        
        const secureUrl = await uploadToCloudinary(file, 'carousel-images');
        

        
        return secureUrl;
    } catch (error) {
        console.error("Error uploading carousel image to Cloudinary: ", error);
        throw error;
    }
};

// Carousel Image Functions
export const getCarouselImages = (setCarouselImages) => {
    let carouselQuery = query(CarouselRef);
    
    onSnapshot(carouselQuery, (response) => {
        setCarouselImages(response.docs.map((doc) => {
            return { ...doc.data(), id: doc.id }
        }))
    })
}

// Add a new carousel image to Firebase
export const addCarouselImage = async (imageData) => {
    try {
        const docRef = await addDoc(CarouselRef, {
            title: imageData.title,
            imageUrl: imageData.imageUrl,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        
        
        return { ...imageData, id: docRef.id };
    } catch (error) {
        console.error("Error adding carousel image: ", error);
        throw error;
    }
};

// Update an existing carousel image in Firebase
export const updateCarouselImage = async (imageId, imageData) => {
    try {
        const imageRef = doc(firestore, "Carousel", imageId);
        await updateDoc(imageRef, {
            title: imageData.title,
            imageUrl: imageData.imageUrl,
            updatedAt: new Date().toISOString()
        });
        
        
        return { ...imageData, id: imageId };
    } catch (error) {
        console.error("Error updating carousel image: ", error);
        throw error;
    }
};

// Delete a carousel image from Firebase
export const deleteCarouselImage = async (imageId) => {
    try {
        const imageRef = doc(firestore, "Carousel", imageId);
        await deleteDoc(imageRef);
        
        
        return true;
    } catch (error) {
        console.error("Error deleting carousel image: ", error);
        throw error;
    }
};

// Get a single carousel image by ID
export const getCarouselImageById = async (imageId) => {
    try {
        const imageRef = doc(firestore, "Carousel", imageId);
        const imageDoc = await getDoc(imageRef);
        
        if (imageDoc.exists()) {
            return { ...imageDoc.data(), id: imageDoc.id };
        } else {
            console.log("No such carousel image!");
            return null;
        }
    } catch (error) {
        console.error("Error getting carousel image: ", error);
        throw error;
    }
};

// Upload chef image to Cloudinary
export const uploadChefImageToCloudinary = async (file) => {
    try {
        const validationErrors = validateImageFile(file);
        if (validationErrors.length > 0) {
            throw new Error(validationErrors.join(', '));
        }
        // console.log('Starting Cloudinary upload for chef image:', file.name);
        const secureUrl = await uploadToCloudinary(file, 'chef-images');
        // console.log('Chef image uploaded successfully to Cloudinary');
        // console.log('Cloudinary URL:', secureUrl);
        return secureUrl;
    } catch (error) {
        console.error("Error uploading chef image to Cloudinary: ", error);
        throw error;
    }
};

// Basic Info Functions

export const getBasicInfoAdmin = (setBasicInfo) => {
    let basicInfoQuery = query(BasicInfoRef);
    
    onSnapshot(basicInfoQuery, (response) => {
        setBasicInfo(response.docs.map((doc) => {
            return { ...doc.data(), id: doc.id }
        }))
    })
}

// Update basic info in Firebase (creates if doesn't exist)
export const updateBasicInfo = async (infoData) => {
    try {
        // Get existing basic info
        const basicInfoQuery = query(BasicInfoRef);
        const querySnapshot = await getDocs(basicInfoQuery);
        
        if (querySnapshot.empty) {
            // Create new basic info document
            const docRef = await addDoc(BasicInfoRef, {
                address: infoData.address,
                email: infoData.email,
                contactNo: infoData.contactNo,
                chefImageUrl: infoData.chefImageUrl,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            console.log("Basic info created with ID: ", docRef.id);
        } else {
            // Update existing basic info document
            const docRef = doc(firestore, "BasicInfo", querySnapshot.docs[0].id);
            await updateDoc(docRef, {
                address: infoData.address,
                email: infoData.email,
                contactNo: infoData.contactNo,
                chefImageUrl: infoData.chefImageUrl,
                updatedAt: new Date().toISOString()
            });
            // console.log("Basic info updated");
        }
        
        return infoData;
    } catch (error) {
        console.error("Error updating basic info: ", error);
        throw error;
    }
};